import { useReselector } from '@/utils/sharedHooks'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { selectCurrentAnnualTaxDetails } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { TypographyStyles } from '@/styles'
import { Link, List, Text } from '@/ui'

export const Terms1120S = ({ currentTaxYear }: { currentTaxYear: string }) => {
  const activeDetails = useReselector(selectCurrentAnnualTaxDetails)

  const scorpVerificationDueDate = convertUtcToLocalDate(
    activeDetails?.tax_entity_verification_extended_due_date
  )

  if (!activeDetails) return null

  const form1040IRSDueDate = convertUtcToLocalDate(
    activeDetails.form_1040_irs_due_date
  )?.endOf('day')

  const form1120sExtensionRequestDueDate = convertUtcToLocalDate(
    activeDetails.form_1120_s_extension_survey_due_date
  )?.endOf('day')

  return (
    <div className="flex flex-col space-y-4">
      <Text variant={TypographyStyles.H2} className="w-full">
        1. You must file an extension request
      </Text>

      <Text className="w-full min-h-fit">
        In order to ensure that Heard has enough time to get all of your{' '}
        {currentTaxYear} books in order, we will require that you file an
        extension with the IRS as a precautionary measure to give you more time
        to file your return, if needed. If you&apos;re able to provide Heard
        with all of the necessary information quickly, it is possible that you
        can still file before the normal{' '}
        {form1040IRSDueDate?.toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)}{' '}
        deadline - but the extension request will ensure that you are protected
        from failure to file penalties just in case.
      </Text>

      <Text variant={TypographyStyles.H2} className="w-full">
        2. You must have a payroll system in place.
      </Text>

      <Text className="w-full min-h-fit">
        If you don&apos;t have one already, Heard makes it easy for you to
        enroll using our embedded payroll system powered by Gusto. This can be
        done directly in your Heard platform.
      </Text>

      <Text variant={TypographyStyles.H2} className="w-full">
        3. You must verify your S-corporation status by{' '}
        {scorpVerificationDueDate?.toFormat(
          DATE_FORMATS_LUXON.DISPLAY_LONG_SHORT_DAY_FULL
        )}
        .
      </Text>

      <Text className="w-full min-h-fit">
        You&apos;ll need to upload either your CP261 notice or Form 385C, which
        you can obtain from the IRS.
      </Text>

      <Text variant={TypographyStyles.H2} className="w-full">
        4. You must complete the extension request survey by{' '}
        {form1120sExtensionRequestDueDate?.toFormat(
          DATE_FORMATS_LUXON.DISPLAY_LONG_SHORT_DAY_FULL
        )}
        .
      </Text>

      <Text className="w-full min-h-fit">
        This helps you to avoid a &quot;failure to file&quot; penalty.
      </Text>

      <Text variant={TypographyStyles.H2} className="w-full">
        5. You may still be responsible for some interest and penalties.
      </Text>

      <Text>
        {/* Payments are still due by the 1040 filing deadline, not 1120-s filing deadline */}
        We will help you file an extension to avoid the “failure to file”
        penalty, but a “failure to pay” penalty may still apply if you underpaid
        your {currentTaxYear} quarterly tax payments, or do not pay the balance
        owed by{' '}
        {form1040IRSDueDate?.toFormat(
          DATE_FORMATS_LUXON.DISPLAY_LONG_SHORT_DAY_FULL
        )}
        .{' '}
        <Link
          href="https://support.joinheard.com/hc/en-us/articles/12962191242007-Filing-an-extension-for-your-1040-individual-income-tax-return"
          newPage
        >
          Learn more
        </Link>
      </Text>

      <Text variant={TypographyStyles.H2}>
        6. After filing an extension, we recommend you complete all of the below
        ASAP.
      </Text>

      <div className="w-full min-h-fit">
        <List bulleted>
          <List.Item>
            <Text>Upload documents for catch up bookkeeping</Text>
          </List.Item>

          <List.Item>
            <Text>Completing your end-of-year accounting wrap up</Text>
          </List.Item>

          <List.Item>
            <Text>
              Complete your tax checklist to be connected to a tax preparer
            </Text>
          </List.Item>
        </List>
      </div>
    </div>
  )
}
